import { DateTime } from 'luxon';
import { defineMessages } from 'react-intl';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';
import type { StudentCollectionType } from '@/helpers/collectionTypes';
import { organizationCanReceiveStudentTickets } from '@/helpers/organizationStatus';

export type PeriodStatus = 'upcoming' | 'active' | 'past';

export const periodStatusMessages = defineMessages({
  upcoming: {
    id: 'period.status.upcoming',
    defaultMessage: 'Kommande',
  },
  active: {
    id: 'period.status.active',
    defaultMessage: 'Aktiv',
  },
  past: {
    id: 'period.status.past',
    defaultMessage: 'Avslutad',
  },
});

export const getPeriodStatus = (claim: {
  valid_from?: string;
  valid_to?: string;
}): PeriodStatus => {
  const now = DateTime.now();
  const from = claim.valid_from
    ? DateTime.fromISO(claim.valid_from).startOf('day')
    : null;
  const to = claim.valid_to ? DateTime.fromISO(claim.valid_to) : null;

  if (from && from > now) return 'upcoming';
  if (to && to < now) return 'past';
  return 'active';
};

export const hasCurrentPeriod = (claims: CollectionTicketClaimDTO[]): boolean =>
  claims.some((claim) => getPeriodStatus(claim) !== 'past');

export const countOrganizationsMissingCurrentPeriod = (
  organizations: OrganizationDTO[],
  claims: CollectionTicketClaimDTO[],
  collectionType: StudentCollectionType,
): number => {
  const claimsByOrganization = new Map<string, CollectionTicketClaimDTO[]>();
  for (const claim of claims) {
    const existing = claimsByOrganization.get(claim.organization_id);
    if (existing) {
      existing.push(claim);
    } else {
      claimsByOrganization.set(claim.organization_id, [claim]);
    }
  }

  return organizations.reduce((count, organization) => {
    if (!organizationCanReceiveStudentTickets(organization)) return count;

    const enabled = organization.enabled_collection_types ?? [];
    if (!enabled.includes(collectionType)) return count;

    const organizationClaims = claimsByOrganization.get(organization.id) ?? [];
    return hasCurrentPeriod(organizationClaims) ? count : count + 1;
  }, 0);
};
